import React, { useState } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import ParentCardNoCol from '../../common/ParentCardNoCol';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { showAlertHtml } from '../../../common/utils/alert';
import CustomLoader from '../../common/CustomLoader';
import { toast } from 'react-toastify';

const ImportProject = ({ projectId, version, actions }) => {
  const [file, setFile] = useState(null);
  const [exportLoadingState, setExportLoadingState] = useState(false);
  const [importLoadingState, setImportLoadingState] = useState(false);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length) {
      setFile(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: '.zip',
  });

  const removeFile = (e) => {
    e.stopPropagation();
    setFile(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    console.log(
      'API Request URL:',
      `${process.env.REACT_APP_BUILDER_ENGINE}projects/core/${projectId}/import-project`,
    );

    const preConfirm = async () => {
      try {
        setImportLoadingState(true);
        await actions.importProject({ projectId, formData });
        setFile(null);
      } catch (error) {
        console.error('Error importing project:', error);
      } finally {
        setImportLoadingState(false);
      }
    };

    showAlertHtml(
      'Import Project?<br/><br/><span>Do you want to import this project?</span>',
      preConfirm,
      'Yes, Import',
      'info',
    );
    console.log('Import initiated!');
  };

  const handleExport = async () => {
    const preConfirm = async () => {
      try {
        setExportLoadingState(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BUILDER_ENGINE}projects/core/${projectId}/export-project`,
          { responseType: 'blob' },
        );
        if (response.status !== 200) {
          throw new Error(`Failed with status: ${response.status}`);
        }
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/zip' }),
        );
        const a = document.createElement('a');
        a.href = url;
        a.download = `project_${projectId}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast.success('Project exported successfully! Download started.');
      } catch (error) {
        console.error('Error exporting project:', error);
        let errorMessage = '❌ Project export failed! Please try again.';
        if (error.response) {
          errorMessage = `❌ Error ${error.response.status}: ${
            error.response.data.message || 'Failed to export project'
          }`;
        } else if (error.message) {
          errorMessage = `❌ ${error.message}`;
        }
        toast.error(errorMessage);
      } finally {
        setExportLoadingState(false);
      }
    };
    showAlertHtml(
      'Export Project?<br/><br/><span>Do you want to export this project?</span>',
      preConfirm,
      'Yes, Export',
      'info',
    );
  };

  return (
    <ParentCardNoCol
      title="Import Project"
      icon="importProject"
      bgColor="drapcode-violet"
      CtaComponent={
        <button
          className={'btn btn-theme-submit btn-md'}
          onClick={handleExport}
          disabled={exportLoadingState}
        >
          <i className={`wb webconnect-box-arrow-up`}></i>&nbsp;Export Project&nbsp;
          <CustomLoader loading={exportLoadingState} />
        </button>
      }
    >
      <div>
        <div
          {...getRootProps()}
          style={{
            border: '2px dashed #ccc',
            padding: '20px',
            cursor: 'pointer',
            textAlign: 'center',
            backgroundColor: '#edebeb',
          }}
        >
          <input {...getInputProps()} />
          {file ? (
            <div style={{ position: 'relative' }}>
              <b>Selected File: {file.name}</b>
              <button
                type="button"
                onClick={removeFile}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: 'transparent',
                  border: 'none',
                  color: 'black',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>
          ) : (
            <b>📂 Drag & drop a ZIP file here or click to select one</b>
          )}
        </div>
        <div className="col-md-12 col-lg-12 mt-2 text-right d-flex justify-content-between mt-4">
          <Link
            to={`/2.0/app-setting/${version}/v/${projectId}/setting/app-config`}
            className="btn btn-outline btn-md"
          >
            <i className="wb webconnect-times"></i> Cancel
          </Link>
          <button
            className={!file ? 'btn btn-default btn-md' : 'btn btn-theme-submit btn-md'}
            onClick={handleUpload}
            disabled={!file || importLoadingState}
          >
            <i className={`wb ${drapcodeIcon('fileUpload')}`}></i>&nbsp; Upload ZIP&nbsp;
            <CustomLoader loading={importLoadingState} />
          </button>
        </div>
      </div>
    </ParentCardNoCol>
  );
};

export default ImportProject;
